/// <amd-module name="Agile/ArtifactPicker/Components/AgileArtifactPicker" />
import * as Resources from "Agile/ArtifactPicker/Resources";
import * as React from "react";
import { Button } from "VSSUI/Button";
import { IPickListAction, IPickListGroup, IPickListIndicator, IPickListItem, IPickListProps } from "VSSUI/Components/PickList";
import { Icon } from "VSSUI/Icon";
import { IItemIndicatorProps } from "VSSUI/ItemIndicator";
import { css } from "VSSUI/Util";
import { IVssIconProps } from "VSSUI/VssIcon";

import { ITeamArtifact } from "./AgileArtifactPickerContracts";
import { AgileArtifactPickListCallout } from "./AgileArtifactPickListCallout";

interface IAgileArtifactPickerProps {
    headerTeam: ITeamArtifact;
    memberTeams: ITeamArtifact[];
    favoritedTeams: ITeamArtifact[];
    iconProps: IVssIconProps;
    pickListClassName: string;
    memberTeamsGroupName: string;
    browseAllText: string;
    onBrowseAllClick: (ev: any) => void;
    onFavoriteClick: (team: ITeamArtifact) => () => void;
    searchResultsLoadingText?: string;
    noItemsText?: string;
    itemAriaDescription?: string;
    searchNoResultsText?: string;
    searchResultsGroupName?: string;
    searchTextPlaceholder?: string;
}

interface IAgileArtifactPickerState {
    calloutIsVisible: boolean;
}

interface IGroupedTeamArtifact extends ITeamArtifact {
    groupKey: string;
}

const MINE_GROUP = "MINE";
const FAVOURITES_GROUP = "FAVOURITES";

export class AgileArtifactPicker extends React.Component<IAgileArtifactPickerProps, IAgileArtifactPickerState> {
    private calloutAnchorElement?: HTMLDivElement;

    constructor(props: IAgileArtifactPickerProps) {
        super(props);

        this.state = { calloutIsVisible: false };
    }

    public render(): JSX.Element | null {
        const {
            headerTeam,
            memberTeams,
            favoritedTeams,
            iconProps,
            pickListClassName,
            searchResultsLoadingText,
            noItemsText,
            itemAriaDescription,
            searchNoResultsText,
            searchResultsGroupName,
            searchTextPlaceholder,
            onFavoriteClick
        } = this.props;

        // merge member and favorited teams and set group key
        const items: IGroupedTeamArtifact[] = memberTeams
            .map(t => {
                return { ...t, groupKey: MINE_GROUP };
            })
            .concat(
                favoritedTeams.map(t => {
                    return { ...t, groupKey: FAVOURITES_GROUP };
                })
            );

        const pickListProps: IPickListProps = {
            items: items,
            initiallySelectedItems: [headerTeam],
            getListItem: this.getListItem,
            groups: this.getGroups(),
            indicators: this.getIndicators(),
            searchResultsLoadingText,
            noItemsText,
            itemAriaDescription,
            searchNoResultsText,
            searchResultsGroupName,
            searchTextPlaceholder,
            width: 400,
            onFocusLost: this.onDismiss,
            isSearchable: true,
            shouldVirtualize: (items: any[]) => {
                return (items || []).length > 50;
            },
            getActions: this.getActions
        };

        return (
            <div className={"agile-artifact-picker"}>
                <div
                    className={"agile-artifact-picker-header"}
                    onClick={this.openDropdown}
                    ref={element => {
                        this.calloutAnchorElement = element || undefined;
                    }}
                >
                    <Icon {...iconProps} className={"agile-artifact-picker-header-icon"} />
                    <div className={"agile-artifact-picker-header-title"}>{headerTeam.name}</div>
                    <Icon className={"agile-artifact-picker-header-chevron"} iconName={"ChevronDown"} />
                </div>
                <Button iconProps={this.getFavouriteStarIcon(headerTeam)} onClick={onFavoriteClick(headerTeam)} subtle={true} />
                {this.state.calloutIsVisible && (
                    <AgileArtifactPickListCallout
                        pickListClassName={pickListClassName}
                        anchorElement={this.calloutAnchorElement}
                        onDismiss={this.onDismiss}
                        picklistProps={pickListProps}
                    />
                )}
            </div>
        );
    }

    private getListItem = (team: IGroupedTeamArtifact): IPickListItem => {
        return {
            key: `${team.groupKey}-${team.id}`,
            name: team.name,
            iconProps: this.props.iconProps,
            href: team.href,
            groupKey: team.groupKey
        } as IPickListItem;
    };

    private getGroups = (): IPickListGroup[] => {
        const { memberTeamsGroupName } = this.props;
        let groups: IPickListGroup[] = [
            {
                key: FAVOURITES_GROUP,
                name: Resources.favoritesGroupName
            },
            {
                key: MINE_GROUP,
                name: memberTeamsGroupName
            }
        ];
        return groups;
    };

    public getIndicators = (): IPickListIndicator[] | undefined => {
        return [
            {
                getItemIndicator: this.getItemsFavouritesIndicator
            }
        ];
    };

    private getFavouriteStarIcon = (team: ITeamArtifact): IVssIconProps => {
        return {
            iconName: team.isFavorited ? "FavoriteStarFill" : "FavoriteStar",
            className: "agile-artifact-picker-favorite-star"
        };
    };

    private getItemsFavouritesIndicator = (team: ITeamArtifact): IItemIndicatorProps => {
        const { onFavoriteClick } = this.props;
        const iconProps = this.getFavouriteStarIcon(team);
        // add picklist specific icon classes
        iconProps.className = css(
            iconProps.className,
            "agile-artifact-pickList-favorite-star",
            !team.isFavorited && "agile-artifact-pickList-favorite-star--notFavorited"
        );
        return {
            title: team.isFavorited ? Resources.removeFromFavorites : Resources.addToFavorites,
            iconProps: iconProps,
            onClick: onFavoriteClick && onFavoriteClick(team)
        };
    };

    public getActions = (): IPickListAction[] => {
        const { browseAllText, onBrowseAllClick } = this.props;

        // add browse all action to the end if a click handler specified
        return onBrowseAllClick
            ? [
                  {
                      onClick: onBrowseAllClick,
                      name: browseAllText,
                      iconProps: {
                          iconName: "Home"
                      }
                  }
              ]
            : [];
    };

    public openDropdown = () => {
        this.setState({ calloutIsVisible: true });
    };

    public closeDropdown = () => {
        this.setState({ calloutIsVisible: false });
    };

    public toggleDropdown = () => {
        if (this.state.calloutIsVisible) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    };

    private onDismiss = () => {
        this.closeDropdown();
    };
}
